import { Timestamp } from "firebase/firestore";

/**
 * Format a Date in local time as YYYYMMDD{separator}HHMMSS
 */
export function formatLocalDateTime(date: Date, separator = ""): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}${separator}${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
}

/**
 * Format a Firestore Timestamp into a string for filenames
 * Format: YYYYMMDD-HHMMSS
 */
export function formatTimestampForFilename(timestamp: Timestamp): string {
  return formatLocalDateTime(timestamp.toDate(), "-");
}

/**
 * Percent-encode the characters Windows forbids in file names (`< > : " \ | ? *` and control
 * characters), plus `%` itself, in a file-name segment that comes from Firestore. Plot IDs are
 * user-supplied doc IDs, so they can contain these (e.g. "Salinas: 1-1" -> "Salinas%3A 1-1"), and
 * Windows cannot extract a ZIP entry whose name includes one. Encoding `%` keeps the mapping
 * one-to-one, so two IDs never share a file name and `decodeURIComponent` recovers the original.
 * `/` cannot occur in a Firestore doc ID.
 */
export function encodeFilenameSegment(segment: string): string {
  // eslint-disable-next-line no-control-regex
  return segment.replace(
    /[<>:"\\|?*%\x00-\x1f]/g,
    (char) => `%${char.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0")}`
  );
}

/**
 * Generate a standardized filename for media files (images, audio, etc.)
 *
 * Format: {plotId}--{type}--sample{sampleKey}--{timestamp}--{collectionId}.{extension}
 * Example: plot123--imageMeasurement--sample1--20240315-143022--abc123def456.jpg
 *
 * The collectionId (Firestore EntryCollection document ID) ensures uniqueness when
 * multiple collectors submit data for the same plot within the same second.
 *
 * This function is used by both CSV export (for image path references) and
 * COCO export (for matching annotations to image files).
 *
 * @param plotId - Plot identifier
 * @param mediaType - Type of media (e.g., "text", "photo", "imageMeasurement", "audio")
 * @param sampleKey - Sample number as string (e.g., "1", "2")
 * @param createdAt - Timestamp when the entry was created
 * @param extension - File extension without dot (e.g., "jpg", "png", "m4a")
 * @param collectionId - Firestore EntryCollection document ID for uniqueness
 * @returns Standardized filename
 */
export function generateMediaFilename(
  plotId: string,
  mediaType: string,
  sampleKey: string,
  createdAt: Timestamp,
  extension: string,
  collectionId: string
): string {
  const timestamp = formatTimestampForFilename(createdAt);
  return `${encodeFilenameSegment(plotId)}--${mediaType}--sample${encodeFilenameSegment(sampleKey)}--${timestamp}--${encodeFilenameSegment(collectionId)}.${extension}`;
}

/**
 * Filename for a plot's transcription clips joined into one WAV:
 * {plotId}--transcription--combined--{timestamp}.wav
 */
export function generateCombinedAudioFilename(plotId: string, timestamp: Timestamp): string {
  return `${encodeFilenameSegment(plotId)}--transcription--combined--${formatTimestampForFilename(timestamp)}.wav`;
}

/**
 * Extract file extension from a cloud storage path
 * @param cloudPath - Full cloud storage path
 * @returns File extension without dot, or default extension
 */
export function getExtensionFromPath(cloudPath: string, defaultExt: string = "jpg"): string {
  const pathParts = cloudPath.split(".");
  return pathParts.length > 1 ? pathParts[pathParts.length - 1] : defaultExt;
}
